import React, { useState } from "react";
import { TextField, InputAdornment, Popper, Icon } from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

interface RPTimePickerProps {
  value?: string;
  onChange(_value: string): void;
  size?: "small" | "medium";
  error?: boolean;
  helperText?: React.ReactNode;
}

const StyledAutocomplete = styled(Autocomplete as any)(({ theme }: any) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "var(--neutral-white)",
    color: (theme.vars || theme).palette.text.primary,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--neutral-500)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--neutral-600)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme.vars || theme).palette.primary.main,
      borderWidth: 2,
    },
    "&.Mui-focused .MuiAutocomplete-clearIndicator": {
      color: "inherit",
    },
    "&:hover .MuiAutocomplete-clearIndicator": {
      color: "inherit",
    },
  },
}));

// Helper to format time as hh:mm AM/PM
const formatTime = (hours: number, minutes: number) => {
  const isPM = hours >= 12;
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  return `${formattedHours}:${formattedMinutes} ${isPM ? "PM" : "AM"}`;
};

// Function to convert am/pm to AM/PM and ensure correct formatting
export const formatToAMPM = (time: string) => {
  let formattedTime = time.replace(/[^0-9:/APM\s]/gi, "");

  // Convert am/pm to AM/PM (ensure it's uppercase)
  formattedTime = formattedTime
    .replace(/\bam\b/g, "AM")
    .replace(/\bpm\b/g, "PM");

  if (/(\d{1,2}):(\d{2})([APap][Mm])?/.test(formattedTime)) {
    return formattedTime;
  }

  if (/(\d{1,2}):(\d{2})/.test(formattedTime)) {
    const [hour, minute] = formattedTime.split(":");
    return formatTime(parseInt(hour, 10), parseInt(minute, 10));
  }

  return formattedTime;
};

// Generate times like "3:00 AM", "3:15 AM", etc.
function generateTimeOptions(interval = 15) {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minutes = 0; minutes < 60; minutes += interval) {
      times.push(formatTime(hour, minutes));
    }
  }
  return times;
}

// Validate hh:mm AM/PM format
const isValidTimeFormat = (time: string) =>
  /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i.test(time.trim());

const timeOptions = generateTimeOptions();

const RPTimePicker = ({
  value,
  onChange,
  size = "small",
  error,
  helperText,
}: RPTimePickerProps) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [isError, setIsError] = useState(false);

  // Function to handle input changes
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    const formattedInput = formatToAMPM(newInputValue);

    setInputValue(formattedInput);

    if (isValidTimeFormat(formattedInput)) {
      setIsError(false);
      onChange(formattedInput);
    } else {
      setIsError(true);
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent,
    newValue: string | null
  ) => {
    if (newValue) {
      setInputValue(newValue);
      setIsError(false);
      onChange(newValue);
    }
  };

  return (
    <StyledAutocomplete
      size={size}
      freeSolo
      options={timeOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="HH:MM AM/PM"
          error={isError || error}
          helperText={isError ? "Invalid time format" : helperText || ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Icon component={RpSvgIcon} name="clock" color="primary" />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            sx: {
              color: (theme) => (theme.vars || theme).palette.text.primary,
            },
          }}
        />
      )}
      PopperComponent={(props) => (
        <Popper {...props} style={{ zIndex: 1300 }} />
      )}
    />
  );
};

export default RPTimePicker;
